/**
 * Icon-only button. SPEC.md §19, §18.4.
 *
 * `label` is required, not optional: an icon with no accessible name is the most
 * likely way this UI could fail a screen reader, so the type system asks for one.
 * The icon itself is aria-hidden — it would otherwise be read out as emoji. §19
 *
 * It composes Button's class constants rather than Button itself, so square
 * sizing does not have to fight Button's padding utilities.
 */

import type { ComponentPropsWithRef, ReactNode } from 'react';
import {
  buttonBaseClasses,
  buttonSelectedClasses,
  buttonVariantClasses,
  type ButtonVariant,
} from './Button';

export interface IconButtonProps
  extends Omit<ComponentPropsWithRef<'button'>, 'children' | 'aria-label'> {
  /** Accessible name — becomes both aria-label and the tooltip. */
  label: string;
  children: ReactNode;
  variant?: ButtonVariant;
  selected?: boolean;
}

export function IconButton({
  label,
  children,
  variant = 'ghost',
  selected,
  type = 'button',
  className,
  ...rest
}: IconButtonProps) {
  const classes = [
    buttonBaseClasses,
    buttonVariantClasses[variant],
    // The 48 px minimum target, in both dimensions. §18.4
    'size-tap text-xl',
    selected ? buttonSelectedClasses : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      type={type}
      aria-label={label}
      aria-pressed={selected}
      title={label}
      className={classes}
    >
      <span aria-hidden="true" className="grid place-items-center leading-none">
        {children}
      </span>
    </button>
  );
}
